import React from 'react';
import { useParams, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import ReactMarkdown from 'react-markdown';
import Reveal from '../components/marketing/Reveal';
import DemoCTA from '../components/marketing/DemoCTA';
import { usePosts, usePost } from '../cms/blog';

const BlogPostPage = () => {
  const { slug } = useParams();
  const post = usePost(slug);
  const blogPosts = usePosts();

  // 404 handling
  if (!post) {
    return (
      <>
        <Helmet>
          <title>Post Not Found | EdAI Systems</title>
        </Helmet>
        <div className="min-h-screen pt-24 pb-16 flex items-center justify-center">
          <div className="text-center">
            <h1 className="text-4xl font-bold text-text-primary mb-4">Post Not Found</h1>
            <p className="text-text-secondary mb-8">The article you're looking for doesn't exist.</p>
            <Link
              to="/blog"
              className="inline-flex items-center text-accent-cyan hover:text-accent-cyan/80 transition-colors font-semibold"
            >
              <span className="mr-2">←</span> Back to Insights
            </Link>
          </div>
        </div>
      </>
    );
  }

  // Format date
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  // Get other posts for "More from EdAI Insights"
  const otherPosts = blogPosts.filter(p => p.slug !== slug).slice(0, 2);

  return (
    <>
      <Helmet>
        <title>{`${post.title} | EdAI Systems`}</title>
        <meta name="description" content={post.description} />
        <meta property="og:title" content={post.title} />
        <meta property="og:description" content={post.description} />
        <meta property="og:type" content="article" />
        <meta property="og:image" content="https://edaisystems.com/og-image.png" />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:image" content="https://edaisystems.com/og-image.png" />
      </Helmet>

      <div className="min-h-screen pt-24 pb-16">
        {/* Back link */}
        <section className="px-4 sm:px-6 lg:px-8 mb-8">
          <div className="max-w-3xl mx-auto">
            <Link
              to="/blog"
              className="inline-flex items-center text-text-muted hover:text-accent-cyan transition-colors text-sm"
            >
              <span className="mr-2">←</span> Back to Insights
            </Link>
          </div>
        </section>

        {/* Article header */}
        <Reveal>
          <section className="px-4 sm:px-6 lg:px-8 mb-12">
            <div className="max-w-3xl mx-auto">
              {/* Category badge */}
              <span
                className={`inline-flex items-center px-3 py-1 text-xs font-mono font-medium uppercase tracking-wider rounded-full border mb-6 bg-${post.categoryColor}/10 text-${post.categoryColor} border-${post.categoryColor}/20`}
                style={{
                  backgroundColor: post.categoryColor === 'accent-cyan' ? 'rgba(56,189,248,0.1)'
                    : post.categoryColor === 'accent-red' ? 'rgba(239,68,68,0.1)'
                    : 'rgba(251,191,36,0.1)',
                  color: post.categoryColor === 'accent-cyan' ? '#38bdf8'
                    : post.categoryColor === 'accent-red' ? '#ef4444'
                    : '#fbbf24',
                  borderColor: post.categoryColor === 'accent-cyan' ? 'rgba(56,189,248,0.2)'
                    : post.categoryColor === 'accent-red' ? 'rgba(239,68,68,0.2)'
                    : 'rgba(251,191,36,0.2)',
                }}
              >
                {post.category}
              </span>

              {/* Title */}
              <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-text-primary mb-6 leading-tight">
                {post.title}
              </h1>

              {/* Meta */}
              <div className="flex flex-wrap items-center gap-2 text-text-muted text-sm">
                <span className="text-text-secondary">{post.author}</span>
                <span>·</span>
                <span>{formatDate(post.date)}</span>
                <span>·</span>
                <span>{post.readTime}</span>
              </div>
            </div>
          </section>
        </Reveal>

        {/* Article content */}
        <article className="px-4 sm:px-6 lg:px-8 mb-16">
          <div className="max-w-3xl mx-auto">
              <ReactMarkdown
                components={{
                  h2: ({ children }) => (
                    <h2 className="text-2xl font-bold text-text-primary mt-12 mb-4">{children}</h2>
                  ),
                  h3: ({ children }) => (
                    <h3 className="text-xl font-bold text-text-primary mt-8 mb-3">{children}</h3>
                  ),
                  p: ({ children }) => (
                    <p className="text-base md:text-lg leading-relaxed mb-6" style={{ color: '#cbd5e1' }}>{children}</p>
                  ),
                  strong: ({ children }) => (
                    <strong className="font-bold" style={{ color: '#f1f5f9' }}>{children}</strong>
                  ),
                  em: ({ children }) => (
                    <em className="italic">{children}</em>
                  ),
                  a: ({ href, children }) => (
                    <a
                      href={href}
                      className="text-accent-cyan hover:underline"
                      target={href.startsWith('/') ? undefined : '_blank'}
                      rel={href.startsWith('/') ? undefined : 'noopener noreferrer'}
                    >
                      {children}
                    </a>
                  ),
                  blockquote: ({ children }) => (
                    <blockquote
                      className="border-l-4 border-accent-cyan pl-6 my-6 italic"
                      style={{ color: '#94a3b8' }}
                    >
                      {children}
                    </blockquote>
                  ),
                  code: ({ inline, children }) => {
                    if (inline) {
                      return (
                        <code
                          className="px-2 py-1 rounded font-mono text-sm"
                          style={{
                            backgroundColor: 'rgba(12,18,32,0.7)',
                            color: '#38bdf8',
                          }}
                        >
                          {children}
                        </code>
                      );
                    }
                    return (
                      <code
                        className="block p-6 rounded-xl font-mono text-base my-8 text-center"
                        style={{
                          backgroundColor: 'rgba(12,18,32,0.7)',
                          color: '#38bdf8',
                          border: '1px solid rgba(56,189,248,0.2)',
                        }}
                      >
                        {children}
                      </code>
                    );
                  },
                  pre: ({ children }) => (
                    <pre className="my-0">{children}</pre>
                  ),
                  ul: ({ children }) => (
                    <ul className="list-disc list-inside space-y-2 mb-6" style={{ color: '#cbd5e1' }}>
                      {children}
                    </ul>
                  ),
                  ol: ({ children }) => (
                    <ol className="list-decimal list-inside space-y-2 mb-6" style={{ color: '#cbd5e1' }}>
                      {children}
                    </ol>
                  ),
                  li: ({ children }) => (
                    <li className="leading-relaxed">{children}</li>
                  ),
                  hr: () => (
                    <hr className="my-12 border-t" style={{ borderColor: 'rgba(51,65,85,0.3)' }} />
                  ),
                  table: ({ children }) => (
                    <div className="overflow-x-auto my-8">
                      <table className="w-full border-collapse rounded-lg overflow-hidden">
                        {children}
                      </table>
                    </div>
                  ),
                  thead: ({ children }) => (
                    <thead style={{ backgroundColor: 'rgba(12,18,32,0.9)' }}>
                      {children}
                    </thead>
                  ),
                  tbody: ({ children }) => (
                    <tbody>{children}</tbody>
                  ),
                  tr: ({ children, ...props }) => (
                    <tr
                      className="border-b"
                      style={{ borderColor: 'rgba(51,65,85,0.3)' }}
                      {...props}
                    >
                      {children}
                    </tr>
                  ),
                  th: ({ children }) => (
                    <th
                      className="px-4 py-3 text-left text-sm font-semibold"
                      style={{ color: '#f1f5f9' }}
                    >
                      {children}
                    </th>
                  ),
                  td: ({ children }) => (
                    <td
                      className="px-4 py-3 text-sm"
                      style={{ color: '#cbd5e1', backgroundColor: 'rgba(12,18,32,0.5)' }}
                    >
                      {children}
                    </td>
                  ),
                }}
              >
                {post.content}
              </ReactMarkdown>
            </div>
          </article>

        {/* Demo CTA */}
        <Reveal delay={100}>
          <section className="py-16 px-4 sm:px-6 lg:px-8">
            <DemoCTA source={`blog-post-${slug}`} />
          </section>
        </Reveal>

        {/* More posts */}
        {otherPosts.length > 0 && (
          <Reveal delay={100}>
            <section className="py-16 px-4 sm:px-6 lg:px-8 bg-bg-card/30">
              <div className="max-w-4xl mx-auto">
                <h2 className="text-2xl font-bold text-text-primary mb-8">More from EdAI Insights</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {otherPosts.map((otherPost) => (
                    <Link
                      key={otherPost.slug}
                      to={`/blog/${otherPost.slug}`}
                      className="group p-6 rounded-2xl border transition-all duration-300 hover:scale-[1.02]"
                      style={{
                        backgroundColor: 'rgba(12,18,32,0.7)',
                        borderColor: 'rgba(51,65,85,0.18)',
                        boxShadow: '0 12px 80px -12px rgba(174, 182, 197, 0.5), 0 0 60px rgba(56,189,248,0.06)',
                      }}
                    >
                      <span
                        className="inline-flex items-center px-2 py-0.5 text-xs font-mono rounded mb-3"
                        style={{
                          backgroundColor: otherPost.categoryColor === 'accent-cyan' ? 'rgba(56,189,248,0.1)'
                            : otherPost.categoryColor === 'accent-red' ? 'rgba(239,68,68,0.1)'
                            : 'rgba(251,191,36,0.1)',
                          color: otherPost.categoryColor === 'accent-cyan' ? '#38bdf8'
                            : otherPost.categoryColor === 'accent-red' ? '#ef4444'
                            : '#fbbf24',
                        }}
                      >
                        {otherPost.category}
                      </span>
                      <h3 className="text-lg font-semibold text-text-primary group-hover:text-accent-cyan transition-colors mb-2 line-clamp-2">
                        {otherPost.title}
                      </h3>
                      <p className="text-text-secondary text-sm line-clamp-2 mb-3">
                        {otherPost.description}
                      </p>
                      <div className="text-text-muted text-xs">
                        {otherPost.readTime}
                      </div>
                    </Link>
                  ))}
                </div>
              </div>
            </section>
          </Reveal>
        )}
      </div>
    </>
  );
};

export default BlogPostPage;
